/**
 * Firestore implementation of WorkOrderRepository.
 *
 * Path: workOrders/{id}
 *
 * Work Orders — internal authorisation to fabricate / install a scope of an
 * order. See src/types/workOrder.ts. findByAssignee / findScheduledBetween
 * stay unimplemented (YAGNI — no scheduling layer yet).
 */
import {
  collection,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../../lib/firebase';
import type { WorkOrder, EntityId } from '../../../types';
import type { WorkOrderRepository } from '../types';

const COLLECTION = 'workOrders';

export const firestoreWorkOrderRepository: WorkOrderRepository = {
  async findById(id: EntityId): Promise<WorkOrder | null> {
    const snap = await getDoc(doc(db, COLLECTION, id));
    return snap.exists() ? (snap.data() as WorkOrder) : null;
  },

  async save(entity: WorkOrder): Promise<void> {
    await setDoc(doc(db, COLLECTION, entity.id), entity);
  },

  async delete(id: EntityId): Promise<void> {
    await deleteDoc(doc(db, COLLECTION, id));
  },

  async list(): Promise<WorkOrder[]> {
    const snap = await getDocs(collection(db, COLLECTION));
    return snap.docs.map((d) => d.data() as WorkOrder);
  },

  async findByOrder(orderId: EntityId): Promise<WorkOrder[]> {
    // Single-field query on `orderId`. The dual-field (`projectId` OR `orderId`)
    // migration read was retired 2026-09-04 (backlog #80): prod carried zero
    // `projectId`-only docs and the extra query ran on every order-detail visit.
    const snap = await getDocs(query(collection(db, COLLECTION), where('orderId', '==', orderId)));
    return snap.docs.map((d) => d.data() as WorkOrder);
  },

  findByAssignee(): Promise<WorkOrder[]> {
    return Promise.reject(
      new Error('workOrderRepository.findByAssignee() not implemented (YAGNI — no scheduling).'),
    );
  },

  findScheduledBetween(): Promise<WorkOrder[]> {
    return Promise.reject(
      new Error(
        'workOrderRepository.findScheduledBetween() not implemented (YAGNI — no scheduling).',
      ),
    );
  },
};
